// client/src/components/pages/AmendmentsAllocateTransactions/followSourceFormat.ts
//
// ST-00212: the words and number formats the Source updates, Unmatched corrections and Source
// follow report tabs share. Colours are Tailwind palette classes, which resolve through the theme
// tokens (tailwind.config.js, src/index.css), so every badge reads in light and dark.
import type {
  FieldValue,
  FollowSourceEventType,
  FollowSourceMode,
  ReviewReason,
  SourceUpdateItem,
} from '../../../types/amendmentFollowSource';

/** Review reasons in the order the filter lists them. */
export const REVIEW_REASONS: ReviewReason[] = [
  'not_found',
  'timestamp_changed',
  'ambiguous',
  'target_amended',
  'zero_nett',
  'no_key',
];

/** Short name of a review reason (filter, report). */
export const REVIEW_REASON_LABEL: Record<ReviewReason, string> = {
  not_found: 'Ticket no longer in the source',
  no_key: 'Ticket number or weigh time unknown',
  ambiguous: 'More than one matching ticket',
  timestamp_changed: 'Weigh time changed',
  target_amended: 'Matching ticket already corrected',
  zero_nett: 'Correction sets the nett weight to 0',
};

/** Why a correction with this reason is not attached by itself, in plain words. */
export const REVIEW_REASON_HELP: Record<ReviewReason, string> = {
  not_found: 'No ticket with this number is in the source any more (removed upstream).',
  no_key: 'The ticket number and weigh time of this correction are not known.',
  ambiguous: 'More than one ticket in the source has this number and weigh time.',
  timestamp_changed: 'The ticket number is in the source with another weigh time.',
  target_amended:
    'The matching ticket already has a correction of its own. Corrections are never merged: archive one of the two.',
  zero_nett:
    'This correction sets the nett weight to 0. A zero is never re-applied by itself: attach it only if the re-issued ticket should count as 0 t.',
};

export const reviewReasonLabel = (reason: string | null | undefined): string =>
  reason && reason in REVIEW_REASON_LABEL
    ? REVIEW_REASON_LABEL[reason as ReviewReason]
    : reason || '—';

/** What a Source updates row is, for its badge. */
export type SourceUpdateKind = 'overtaken' | 'adopted' | 'reattached' | 'attached';

export function sourceUpdateKind(
  item: Pick<SourceUpdateItem, 'eventType' | 'adopted'>
): SourceUpdateKind {
  if (item.eventType === 'reanchored') return 'reattached';
  if (item.eventType === 'review_attached') return 'attached';
  return item.adopted ? 'adopted' : 'overtaken';
}

export const SOURCE_UPDATE_KIND: Record<
  SourceUpdateKind,
  { label: string; title: string; className: string }
> = {
  overtaken: {
    label: 'Overtaken',
    title: 'The source changed this field after your correction: the source value is used now.',
    className: 'bg-amber-50 text-amber-800 ring-amber-200',
  },
  adopted: {
    label: 'Source agrees (adopted)',
    title: 'The source now holds the value you corrected to.',
    className: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  },
  reattached: {
    label: 'Re-attached',
    title: 'The upstream system re-issued the ticket; your correction moved to the new ticket.',
    className: 'bg-sky-50 text-sky-800 ring-sky-200',
  },
  attached: {
    label: 'Attached from review',
    title: 'Someone attached this correction to the ticket from the Unmatched corrections list.',
    className: 'bg-sky-50 text-sky-800 ring-sky-200',
  },
};

/** What a run did to a ticket (report per-ticket table). */
export const EVENT_TYPE_LABEL: Record<FollowSourceEventType, string> = {
  reanchored: 'Re-attached',
  correction_retired: 'Correction retired',
  orphaned: 'To review',
  review_attached: 'Attached from review',
  review_archived: 'Archived from review',
};

export const FOLLOW_SOURCE_MODE: Record<FollowSourceMode, { label: string; help: string }> = {
  off: {
    label: 'Off',
    help: 'The job does not run: corrections are not re-attached or retired by themselves.',
  },
  'dry-run': {
    label: 'Dry run',
    help: 'The job works out what it would change and keeps only its report. No ticket changes.',
  },
  on: {
    label: 'On',
    help: 'The job re-attaches corrections to re-issued tickets and retires corrections the source overtook.',
  },
};

const ISO_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** A field value as the lists show it: blank reads "(blank)", a timestamp loses its "T". */
export function formatFieldValue(value: FieldValue | undefined): string {
  if (value === null || value === undefined || value === '') return '(blank)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' && ISO_LOCAL.test(value))
    return value.replace('T', ' ').slice(0, 19);
  return String(value);
}

const TONNES = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Tonnes with two decimals and a point ("1,234.50 t"); `signed` puts + before a gain. */
export function formatTonnes(value: number | null | undefined, signed = false): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  const text = `${TONNES.format(n)} t`;
  return signed && n > 0 ? `+${text}` : text;
}

/** "Dispatch → Receipt", a dash for a missing end. */
export const routeText = (
  dispatch: string | null | undefined,
  receipt: string | null | undefined
): string => `${dispatch || '—'} → ${receipt || '—'}`;

/** A count with thousands separators. */
export const formatCount = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US');
